/* ==========================================================================
   Sharky — behaviour primitives (2026 CRO)
   Dependency-free. No network patching, no third-party CDNs.

   Responsibilities:
     1. Scroll reveal for [data-sk-reveal] (CSS-driven, JS only toggles a class)
     2. Sticky buy bar for [data-sk-sticky] (future replacement of #sk-sticky)
     3. Header scrolled state (.sk-scrolled on <html>)

   Explicitly NOT here: dialog open/close. The legacy sharky.js already owns
   [data-sk-open] dialogs and the size guide; two handlers would double-fire
   showModal(). Dialog behaviour moves here only when sharky.js is retired.

   Everything degrades to a working page when this file fails to load.
   ========================================================================== */

(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const $ = (sel, ctx = doc) => ctx.querySelector(sel);
  const $$ = (sel, ctx = doc) => Array.from(ctx.querySelectorAll(sel));

  /* --- 1. Reveal ------------------------------------------------------- */
  const reveal = (() => {
    let observer = null;

    const show = (el) => el.classList.add('is-in');

    const connect = () => {
      // Reduced motion keeps the fade; assets/sk-motion.css removes the movement.
      if (observer || designMode || !('IntersectionObserver' in window)) {
        $$('[data-sk-reveal]').forEach(show);
        return;
      }

      observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            show(entry.target);
            observer.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
      );

      $$('[data-sk-reveal]').forEach((el) => {
        if (el.classList.contains('is-in')) return;
        observer.observe(el);
      });
    };

    return {
      init: connect,
      refresh() {
        if (observer) observer.disconnect();
        observer = null;
        connect();
      },
    };
  })();

  /* --- 2. Sticky buy bar ---------------------------------------------- */
  const sticky = (() => {
    let bar = null;
    let sentinel = null;
    let frame = 0;

    const update = () => {
      frame = 0;
      if (!bar || !sentinel) return;
      const passed = sentinel.getBoundingClientRect().bottom <= 0;
      bar.classList.toggle('is-visible', passed);
      bar.toggleAttribute('inert', !passed);
    };

    const request = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };

    const init = () => {
      bar = $('[data-sk-sticky]');
      if (!bar) return;

      // The sentinel is the element the bar should appear after (the buy box).
      sentinel = bar.dataset.skStickySentinel ? $(bar.dataset.skStickySentinel) : null;
      if (!sentinel) {
        sentinel = $('product-form-component') || $('.product-information__media');
      }
      if (!sentinel) return;

      bar.hidden = false;
      bar.toggleAttribute('inert', true);
      update();

      doc.addEventListener('scroll', request, { passive: true, capture: true });
      window.addEventListener('resize', request, { passive: true });
    };

    return { init, request };
  })();

  /* --- 3. Header state ------------------------------------------------- */
  const header = (() => {
    const scroller = $('.page-wrapper');
    let frame = 0;

    const position = () => {
      const container =
        scroller && /auto|scroll/.test(getComputedStyle(scroller).overflowY) ? scroller : window;
      return container === window ? window.scrollY || 0 : container.scrollTop;
    };

    const update = () => {
      frame = 0;
      root.classList.toggle('sk-scrolled', position() > 6);
    };

    return {
      init() {
        doc.addEventListener(
          'scroll',
          () => {
            if (frame) return;
            frame = window.requestAnimationFrame(update);
          },
          { passive: true, capture: true }
        );
        update();
      },
    };
  })();

  /* --- Boot ------------------------------------------------------------ */
  const boot = () => {
    header.init();
    sticky.init();
    reveal.init();
  };

  if (doc.readyState === 'loading') {
    doc.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  // Theme editor: re-attach after section updates.
  doc.addEventListener('shopify:section:load', () => {
    sticky.request();
    reveal.refresh();
  });

  reduceMotion.addEventListener?.('change', reveal.refresh);

  if (designMode) {
    // Keep behaviour predictable while editing.
    root.classList.add('sk-design-mode');
  }
})();
